import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    ListTableSort,
    SortOrder,
    listFiltersService,
    mapToDropdownOption,
    mapToNamedProperty,
    getEnumValues,
    DropdownOption
} from "sprint-common-web";

import { useDistributorsApi } from "../../../../common/api/catalogue-api/distributors/useDistributorsApi";
import { OnPremOrderSource } from "../../../../common/api/orders-api/on-prem-order/GetOnPremOrderListResponse";
import { OrderAirlinesDataContext } from "../../../common/data-providers/OrderAirlinesDataProvider";
import { OnPremOrderStatus } from "../../../common/types/OnPremOrderStatus";
import { OrderCompletionProgress } from "../../../common/types/OrderCompletionProgress";
import { getCompletionProgressOptions } from "../../../common/utils/getCompletionProgressOptions";
import { getUniqueSystemDropdownOptions } from "../../../orders/common/getUniqueSystemDropdownOptions";
import { mapOnPremOrderStatusName } from "../../common/utils/getAvailableOnPremOrderStatuses";
import { OnPremOrderListingContext } from "../context/OnPremOrderListingContext";

export interface OnPremOrderListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    statuses: number[];
    airlineIds?: string[];
    distributorId?: string;
    distributorName?: string;
    systemIds?: string[];
    content?: string;
    pONumber?: string;
    filename?: string;
    progresses?: number[];
    sources?: number[];
    dueDateFrom?: Date;
    dueDateTo?: Date;
    startDateFrom?: Date;
    startDateTo?: Date;
}

const statuses = [
    OnPremOrderStatus.Ordered,
    OnPremOrderStatus.PartialAssetAssigned,
    OnPremOrderStatus.AssetAssigned,
    OnPremOrderStatus.Recalled,
    OnPremOrderStatus.Encoded,
    OnPremOrderStatus.ReadyToSend,
    OnPremOrderStatus.SentToOem,
    OnPremOrderStatus.OnHold,
    OnPremOrderStatus.Cancelled,
    OnPremOrderStatus.Rejected
];

const sources = getEnumValues(OnPremOrderSource);

const defaultFilters: OnPremOrderListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "createdDate",
    sortOrder: "desc",
    statuses,
    airlineIds: undefined,
    distributorId: undefined,
    distributorName: undefined,
    systemIds: undefined,
    content: "",
    pONumber: "",
    filename: "",
    progresses: getEnumValues(OrderCompletionProgress),
    sources,
    dueDateFrom: undefined,
    dueDateTo: undefined,
    startDateFrom: undefined,
    startDateTo: undefined
};

export const useOnPremOrderListingFilters = () => {
    const { listingConfig } = useContext(OnPremOrderListingContext);
    const { airlines, isLoading: areAirlinesLoading } = useContext(OrderAirlinesDataContext);

    const history = useHistory();
    const location = useLocation();

    const filtersService = useMemo(() => listFiltersService<OnPremOrderListingFilters>(defaultFilters), []);

    const [filters, setFilters] = useState<OnPremOrderListingFilters>(() => {
        const fetchedFilters = filtersService.getSavedFilters(location.search);

        return { ...fetchedFilters, pageSize: listingConfig.pageSize ?? fetchedFilters.pageSize };
    });
    const { suggestDistributorsByName } = useDistributorsApi();

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [location.pathname, history, filters]);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setStatuses = (OnPremOrderStatuses: number[]): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({
            ...prevFilters,
            statuses: OnPremOrderStatuses,
            page: 1
        }));
    };

    const setSources = (orderSources: OnPremOrderSource[]): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({
            ...prevFilters,
            sources: orderSources,
            page: 1
        }));
    };

    const setDistributor = (distributorId?: string, distributorName?: string): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({
            ...prevFilters,
            distributorId,
            distributorName,
            page: 1
        }));
    };

    const setAirlines = (options: DropdownOption[]): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({
            ...prevFilters,
            airlineIds: options.map((option) => String(option.key)),
            systemIds: undefined,
            page: 1
        }));
    };

    const setFilename = (filename: string): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, filename, page: 1 }));
    };

    const setSystems = (options: DropdownOption[]): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({
            ...prevFilters,
            systemIds: options.map((option) => option.key),
            page: 1
        }));
    };

    const setContent = (content?: string): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, content, page: 1 }));
    };

    const setPONumber = (pONumber?: string): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, pONumber, page: 1 }));
    };

    const setProgresses = (progresses?: number[]): void => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, progresses, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const setDueDateFrom = (dueDateFrom: Date) => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, dueDateFrom, page: 1 }));
    };

    const setDueDateTo = (dueDateTo: Date) => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, dueDateTo, page: 1 }));
    };

    const setStartDateFrom = (startDateFrom: Date) => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, startDateFrom, page: 1 }));
    };

    const setStartDateTo = (startDateTo: Date) => {
        setFilters((prevFilters: OnPremOrderListingFilters) => ({ ...prevFilters, startDateTo, page: 1 }));
    };

    const statusOptions = statuses.map((status) => ({ key: status, text: mapOnPremOrderStatusName(status) }));

    const sourceOptions = sources.map((source) => ({ key: source, text: OnPremOrderSource[source] }));

    const airlineOptions = airlines.map((airline) => mapToDropdownOption(airline));

    const progressOptions = getCompletionProgressOptions();

    const systemOptions = getUniqueSystemDropdownOptions(airlines, filters.airlineIds);

    const suggestDistributor = async (name: string) => {
        const result = await suggestDistributorsByName({ name });
        return result.items.map((item) => mapToNamedProperty(item));
    };

    const filterDistributorOptions = async (query: string) => {
        const suggestedDistributors = await suggestDistributor(query);

        return suggestedDistributors.map((distributor) => mapToDropdownOption(distributor));
    };

    return {
        filters,
        statusOptions,
        sourceOptions,
        airlineOptions,
        systemOptions,
        progressOptions,
        airlines,
        areAirlinesLoading,
        filterDistributorOptions,
        setPage,
        setPageSize,
        setSort,
        setStatuses,
        setSources,
        setProgresses,
        setDistributor,
        setAirlines,
        setSystems,
        setContent,
        setPONumber,
        setFilename,
        setDueDateFrom,
        setDueDateTo,
        setStartDateFrom,
        setStartDateTo,
        clearFilters
    };
};
